import type { ComponentType } from "react";
import { allFigures, type Figure } from "@howcomet/shared";
import { ArrayFigure } from "./ArrayFigure";
import { CycleFigure } from "./CycleFigure";
import { PartsFigure } from "./PartsFigure";
import { SequenceFigure } from "./SequenceFigure";
import { ContrastFigure } from "./ContrastFigure";
import { ScaleFigure } from "./ScaleFigure";
import { OrbitFigure } from "./OrbitFigure";
import { WaveFigure } from "./WaveFigure";

/**
 * The client half of the figure system. The server chooses and validates; this
 * draws. They meet on the `kind` string, exactly as the shells meet on `type`.
 *
 * Adding a figure here is one import and one entry.
 */

export interface FigureProps<F = Figure> {
  figure: F;
}

const RENDERERS: Record<string, ComponentType<FigureProps<never>>> = {
  array: ArrayFigure as ComponentType<FigureProps<never>>,
  cycle: CycleFigure as ComponentType<FigureProps<never>>,
  parts: PartsFigure as ComponentType<FigureProps<never>>,
  sequence: SequenceFigure as ComponentType<FigureProps<never>>,
  contrast: ContrastFigure as ComponentType<FigureProps<never>>,
  scale: ScaleFigure as ComponentType<FigureProps<never>>,
  orbit: OrbitFigure as ComponentType<FigureProps<never>>,
  wave: WaveFigure as ComponentType<FigureProps<never>>,
};

/*
  Registry drift is silent in production by design — an unknown kind draws
  nothing, which is what lets an old iPad meet a new figure without breaking.
  That same silence would hide a renderer someone forgot to add here, and the
  only symptom would be a picture that never appears. So in dev it is loud.
*/
if (import.meta.env.DEV) {
  const missing = allFigures()
    .map((f) => f.kind)
    .filter((kind) => !Object.hasOwn(RENDERERS, kind));
  if (missing.length > 0) {
    console.error(
      `Figures registered on the server with no renderer here: ${missing.join(", ")}. ` +
        `Add them to packages/app/src/figures/registry.tsx.`,
    );
  }
}

/**
 * Renders a figure, or nothing at all.
 *
 * An unknown kind draws nothing rather than apologising. The shells show "this
 * needs the newest app" because a missing check strands a star behind it; a
 * missing picture strands nothing, and the answer above it is already complete.
 * A child on an older build should see a normal answer, not a hole.
 */
export function FigureView({ figure }: { figure?: Figure }) {
  if (!figure) return null;
  const Renderer = RENDERERS[figure.kind];
  if (!Renderer) return null;

  return (
    <figure className="pop my-4 rounded-3xl border-2 border-lilac/40 bg-panel/60 p-4">
      <Renderer figure={figure as never} />
      {figure.caption && (
        <figcaption className="mt-3 text-center text-[14px] leading-snug text-cream/60">
          {figure.caption}
        </figcaption>
      )}
    </figure>
  );
}
